<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-combo-prices"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="action === 'delete' ? (isSaving ? t('Desligando') : t('Desligar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="action === 'delete' ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div v-if="action === 'delete'" class="delete-confirmation" data-testid="so-combo-prices-delete-confirm">
        <p>{{ t('Desligar este combo nesta unidade?') }}</p>
        <p class="delete-detail">{{ entry?.so_combo_name }}</p>
        <p class="delete-detail">{{ t('Ele sai da venda da unidade na hora. Compras já feitas continuam valendo, e a linha fica aqui para religar.') }}</p>
      </div>

      <s-o-combo-prices-form
        v-else
        ref="formRef"
        :franchise-id="franchiseId"
        :entry="entry"
        @submit-success="handleFormSuccess"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';
import { useSOComboPrices } from '../../composables/useSOComboPrices';
import SOComboPricesForm from './SOComboPricesForm.vue';
import type { ComboPrices_PriceRow } from '../../contracts/ComboPrices_PriceRow';

interface OpenPayload {
  action: 'create' | 'edit' | 'delete';
  franchiseId: number | string | null;
  entry?: ComboPrices_PriceRow;
}

const { t } = useI18n();
const { remove } = useSOComboPrices();

const isVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const action = ref<OpenPayload['action']>('create');
const franchiseId = ref<number | string | null>(null);
const entry = ref<ComboPrices_PriceRow | null>(null);
const formRef = ref<{ handleSubmit: () => Promise<void> } | null>(null);

const modalTitle = computed<string>(() => {
  if (action.value === 'delete') return t('Desligar combo na unidade');
  return action.value === 'edit' ? t('Editar preço do combo') : t('Adicionar preço de combo');
});

const handleClose = (): void => {
  isVisible.value = false;
  isSaving.value = false;
  entry.value = null;
};

const handleSave = async (): Promise<void> => {
  isSaving.value = true;
  try {
    await formRef.value?.handleSubmit();
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async (): Promise<void> => {
  if (!entry.value) return;
  isSaving.value = true;
  try {
    await remove({ so_combo_price_id: entry.value.so_combo_price_id });
    eventBus.emit('globalMessage.success', t('Combo desligado nesta unidade'));
    eventBus.emit('so-combo-prices.saved');
    handleClose();
  } catch (error: unknown) {
    logError('SOComboPricesModal', 'handleDelete', 'Error', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao desligar o combo') + ': ' + message);
  } finally {
    isSaving.value = false;
  }
};

const handleFormSuccess = (): void => {
  eventBus.emit('so-combo-prices.saved');
  handleClose();
};

const handleModalOpen = (data?: OpenPayload): void => {
  if (!data) return;
  action.value = data.action;
  franchiseId.value = data.franchiseId;
  entry.value = data.entry ?? null;
  isVisible.value = true;
};

onMounted(() => {
  eventBus.on<OpenPayload>('modal.so-combo-prices.open', handleModalOpen);
});

onBeforeUnmount(() => {
  eventBus.off<OpenPayload>('modal.so-combo-prices.open', handleModalOpen);
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.delete-detail {
  color: var(--p-text-muted-color);
}
</style>
